import React from "react";
import { projects, demoMailto } from "../data/content";

function StatusChip({ status, tone }) {
  const toneClass =
    tone === "offline"
      ? "border-danger/40 text-danger"
      : tone === "active" || tone === "development"
        ? "border-accent/40 text-accent-strong"
        : "border-rule text-ink-secondary";

  return (
    <span className={`status-chip ${toneClass}`}>
      <span aria-hidden="true">■</span>
      {status}
    </span>
  );
}

function FeaturedProject({ project, index }) {
  const sections = [
    ["Problem", project.caseStudy.problem],
    ["Approach", project.caseStudy.approach],
    ["My role", project.caseStudy.role],
    [project.caseStudy.stateLabel || "Outcome", project.caseStudy.outcome],
  ];

  return (
    <article
      id={`project-${project.id}`}
      className="reveal overflow-hidden border border-rule bg-canvas-raised"
    >
      <div className="grid gap-0 border-b border-rule lg:grid-cols-12">
        <div className="p-6 md:p-10 lg:col-span-5">
          <div className="flex flex-wrap items-center gap-3">
            <p className="label">{project.category}</p>
            <StatusChip status={project.status} tone={project.statusTone} />
          </div>

          <p className="label mt-8">Featured case study / 0{index + 1}</p>
          <h3 className="mt-3 font-display text-[clamp(2.25rem,5vw,4.5rem)] font-semibold leading-[0.96] tracking-[-0.045em]">
            {project.title}
          </h3>
          <p className="mt-3 font-display text-[clamp(1.25rem,2vw,1.75rem)] italic text-ink-secondary">
            {project.subtitle}
          </p>

          <p className="mt-6 max-w-xl text-base leading-7 text-ink-secondary">
            {project.purpose}
          </p>

          {project.context ? (
            <p className="mt-4 max-w-xl text-sm leading-6 text-ink-muted">
              {project.context}
            </p>
          ) : null}

          <div className="mt-8 flex flex-wrap gap-3">
            {project.href ? (
              <a
                href={project.href}
                target="_blank"
                rel="noreferrer"
                className="btn-primary"
              >
                {project.hrefLabel || "View live product"} ↗
              </a>
            ) : null}
            <a
              href={demoMailto(project.title)}
              className="btn-secondary"
              aria-label={`Request project walkthrough for ${project.title}`}
            >
              Request walkthrough →
            </a>
          </div>
        </div>

        <div className="bg-graphite p-3 sm:p-5 lg:col-span-7 lg:p-6">
          <div
            className={
              project.secondaryImage
                ? "grid h-full gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(11rem,0.34fr)]"
                : "h-full"
            }
          >
            <figure className="flex min-h-[18rem] items-center justify-center overflow-hidden border border-white/10 bg-black/20 p-2 sm:min-h-[24rem] md:p-3">
              <img
                src={project.image}
                alt={project.imageAlt}
                width={1600}
                height={1000}
                loading={index === 0 ? "eager" : "lazy"}
                className="max-h-[38rem] w-full object-contain"
              />
            </figure>

            {project.secondaryImage ? (
              <figure className="flex min-h-[16rem] items-center justify-center overflow-hidden border border-white/10 bg-black/20 p-2 lg:min-h-0">
                <img
                  src={project.secondaryImage}
                  alt={project.secondaryImageAlt}
                  width={720}
                  height={1280}
                  loading="lazy"
                  className="max-h-[38rem] w-full object-contain"
                />
              </figure>
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4">
        {sections.map(([label, body], sectionIndex) => (
          <div
            key={label}
            className={`p-6 md:p-8 ${sectionIndex > 0 ? "border-t border-rule md:border-l md:border-t-0" : ""} ${sectionIndex === 2 ? "md:border-t lg:border-t-0" : ""}`}
          >
            <h4 className="label mb-3">{label}</h4>
            <p className="text-sm leading-6 text-ink-secondary">{body}</p>
          </div>
        ))}
      </div>

      <div className="grid border-t border-rule md:grid-cols-2">
        <div className="p-6 md:p-8">
          <h4 className="label mb-3">{project.detailLabel || "Technology"}</h4>
          <ul className="tech-list">
            {project.technologies.map((tech) => (
              <li key={tech}>{tech}</li>
            ))}
          </ul>
        </div>

        <div className="border-t border-rule p-6 md:border-l md:border-t-0 md:p-8">
          <h4 className="label mb-3">{project.secondaryDetailLabel}</h4>
          <ul className="tech-list">
            {project.secondaryDetails.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
}

function ProjectCard({ project }) {
  return (
    <article className="reveal flex h-full flex-col overflow-hidden border border-rule bg-canvas-raised">
      <div className="aspect-[16/10] overflow-hidden border-b border-rule bg-graphite/5">
        <img
          src={project.image}
          alt={project.imageAlt}
          width={1280}
          height={800}
          loading="lazy"
          className="h-full w-full object-cover object-top transition-transform duration-500 hover:scale-[1.015]"
        />
      </div>

      <div className="flex flex-1 flex-col p-6 md:p-7">
        <div className="flex flex-wrap items-center gap-3">
          <p className="label">{project.category}</p>
          <StatusChip status={project.status} tone={project.statusTone} />
        </div>

        <h3 className="mt-4 font-display text-2xl font-semibold tracking-[-0.025em]">
          {project.title}
        </h3>
        <p className="mt-3 text-sm leading-6 text-ink-secondary">
          {project.purpose}
        </p>

        <div className="mt-6 border-t border-rule pt-4">
          <ul className="tech-list">
            {project.technologies.map((tech) => (
              <li key={tech}>{tech}</li>
            ))}
          </ul>
        </div>

        <div className="mt-auto pt-6">
          <a
            href={demoMailto(project.title)}
            className="font-mono text-[0.6875rem] uppercase tracking-[0.06em] text-accent-strong underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
          >
            Request walkthrough →
          </a>
        </div>
      </div>
    </article>
  );
}

export default function Work() {
  const { featured, others } = projects;

  return (
    <section id="work" className="section-pad">
      <div className="wrap-wide">
        <header className="reveal mb-12 grid gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="label">01 / Selected work</p>
            <h2 className="mt-4 font-display text-[clamp(2rem,4vw,3.75rem)] font-semibold leading-[1.02] tracking-[-0.04em]">
              Work that shows how I think, build, and secure.
            </h2>
          </div>
          <p className="max-w-xl text-ink-secondary lg:col-span-5 lg:justify-self-end">
            Public products and security-focused engineering work chosen for
            depth, not volume. The first two show current product work; the
            smaller studies show focused security and delivery experience.
          </p>
        </header>

        <div className="space-y-10">
          {featured.map((project, index) => (
            <FeaturedProject key={project.id} project={project} index={index} />
          ))}
        </div>

        <div className="reveal mt-16 border-t border-rule pt-8">
          <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="label">Additional selected work</p>
              <h3 className="mt-3 font-display text-2xl font-semibold tracking-[-0.025em]">
                Focused tools and delivery projects
              </h3>
            </div>
            <p className="max-w-xl text-sm leading-6 text-ink-muted">
              These projects reinforce the same through-line: understand the
              system, expose useful signals, and make the result maintainable.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {others.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
